'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import logTripPlannerCTAClicked from '@alltrails/amplitude/events/logTripPlannerCTAClicked';
import logTripPlannerCTAViewed from '@alltrails/amplitude/events/logTripPlannerCTAViewed';
import Typography from '@alltrails/core/components/Typography';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import LinkOrNativeAnchor from '@alltrails/denali/components/LinkOrNativeAnchor';
import Chevron from '@alltrails/denali/icons/Chevron';
import {
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  latParamKey,
  lngParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import { getTripPlannerEntryParams, type TripPlannerEntryPoint } from '../../utils/tripPlannerEntryParams';
import type { PlanATripMapBounds, TripPlannerSearchArea } from '../../utils/tripPlannerSearchArea';
import styles from './PlanATripCard.module.scss';
import illustrationSrc from './trip-assistant-illustration.svg';

export type { PlanATripMapBounds };

type PlanATripCardProps = TripPlannerSearchArea & {
  // required when navigating across apps - the trip planner lives in the explore app.
  isNativeLink?: boolean;
  className?: string;
  entryPoint: TripPlannerEntryPoint;
  locationId?: number;
  parkId?: number;
};

const getTripPlannerQuery = ({
  entryPoint,
  locationId,
  mapBounds,
  mapCenter,
  parkId
}: Pick<PlanATripCardProps, 'entryPoint' | 'locationId' | 'mapBounds' | 'mapCenter' | 'parkId'>) => {
  const entryParams = getTripPlannerEntryParams({ entryPoint, locationId, parkId });
  if (mapBounds) {
    return `?${new URLSearchParams({
      [topLeftLatParamKey]: mapBounds.topLeftLat,
      [topLeftLngParamKey]: mapBounds.topLeftLng,
      [bottomRightLatParamKey]: mapBounds.bottomRightLat,
      [bottomRightLngParamKey]: mapBounds.bottomRightLng,
      ...entryParams
    })}`;
  }
  if (mapCenter) {
    return `?${new URLSearchParams({ [latParamKey]: String(mapCenter.lat), [lngParamKey]: String(mapCenter.lng), ...entryParams })}`;
  }
  return `?${new URLSearchParams(entryParams)}`;
};

/**
 * V2 designs: https://www.figma.com/design/jRyJm5tO66lHSa0E8PSox2/H2--26-Trip-planner?node-id=2958-71169&m=dev
 */
const PlanATripCard = ({ mapBounds, mapCenter, isNativeLink, className, entryPoint, locationId, parkId }: PlanATripCardProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const href = wrapUrlSafe(`/trip-assistant${getTripPlannerQuery({ entryPoint, locationId, mapBounds, mapCenter, parkId })}`, languageRegionCode);

  const cardRef = useRef<HTMLAnchorElement>(null);
  const hasLoggedViewRef = useRef(false);
  const isVisible = useIsVisible(cardRef);

  const analyticsAttributes = useMemo(
    () => ({ entry_point: entryPoint, location_id: locationId, park_id: parkId }),
    [entryPoint, locationId, parkId]
  );

  useEffect(() => {
    if (!isVisible || hasLoggedViewRef.current) {
      return;
    }
    hasLoggedViewRef.current = true;
    logTripPlannerCTAViewed(analyticsAttributes);
  }, [analyticsAttributes, isVisible]);

  const onClick = useCallback(() => {
    logTripPlannerCTAClicked(analyticsAttributes);
  }, [analyticsAttributes]);

  return (
    <LinkOrNativeAnchor
      className={classNames(styles.card, className)}
      href={href}
      onClick={onClick}
      ref={cardRef}
      {...(isNativeLink ? { isNativeLink: true } : {})}
    >
      <Image alt="" className={styles.illustration} src={illustrationSrc} />
      <span className={styles.text}>
        <Typography className={styles.title} component="span" variant="text150bold">
          <FormattedMessage defaultMessage="Planning a trip?" />
        </Typography>
        <Typography className={styles.subtitle} component="span" variant="text100">
          <FormattedMessage defaultMessage="Turn any destination into an unforgettable adventure" />
        </Typography>
      </span>
      <Chevron className={styles.chevron} color="--color-text-secondary" orientation="right" size="sm" />
    </LinkOrNativeAnchor>
  );
};

export default PlanATripCard;
